import { FlexRow } from '@/components/common/Flex';
import { BoxProps, FlexProps } from '@chakra-ui/react';
import { Button, ButtonProps } from './Button';
import ButtonWithIcon, { ButtonWithIconProps } from './ButtonWithIcon';
import { useFormContext } from './Form';
import { ArrowLeft } from './icons';

export interface BackNextNavProps extends FlexProps, BoxProps {
    isLoading?: boolean;
    isDisabled?: boolean;
    onBack?: (() => void) | null;
    onNext?: (() => void) | null;
    nextButtonText?: string;
    backButtonText?: string;
    showBackIcon?: boolean;
    shouldFormSubmit?: boolean; // This can only be used if placed inside of a `<Form />`
    buttonProps?: ButtonWithIconProps;
    onClickEventParams: ButtonProps['onClickEventParams'] & {
        location: string;
    };
}

export default function BackNextNav({
    isLoading = false,
    isDisabled = false,
    onBack,
    onNext,
    nextButtonText,
    backButtonText,
    showBackIcon = true,
    shouldFormSubmit,
    buttonProps,
    onClickEventParams,
    children,
    ...props
}: BackNextNavProps) {
    const { submit } = useFormContext();
    const backLabel = backButtonText || 'Back';
    const nextLabel = nextButtonText || 'Next';
    return (
        <FlexRow
            mt={{ base: 3, lg: 4, '2xl': 5 }}
            justify="space-between"
            grow={1}
            width="full"
            align="flex-end"
            {...props}
        >
            {onBack ? (
                <ButtonWithIcon
                    variant="unstyled"
                    iconLeft={
                        showBackIcon ? <ArrowLeft size="sm" /> : undefined
                    }
                    color="gray.500"
                    onClick={onBack}
                    onClickEvent="Back Button Clicked"
                    onClickEventParams={{
                        label: backLabel,
                        ...onClickEventParams,
                    }}
                    mr={{ base: 0, sm: 15 }}
                    size="md"
                    {...buttonProps}
                >
                    {backLabel}
                </ButtonWithIcon>
            ) : (
                <div />
            )}

            <FlexRow align="center" gap={2}>
                {children}
                {(onNext || shouldFormSubmit) && (
                    <Button
                        onClick={shouldFormSubmit ? submit : () => onNext?.()}
                        onClickEvent="Next Button Clicked"
                        onClickEventParams={{
                            label: nextLabel,
                            ...onClickEventParams,
                        }}
                        isLoading={isLoading}
                        disabled={isDisabled}
                        size="md"
                        {...buttonProps}
                    >
                        {nextLabel}
                    </Button>
                )}
            </FlexRow>
        </FlexRow>
    );
}
